import type {
  BankAccount,
  Bets,
  Channel,
  FreebetItem,
  GetAllPlayersRequestDto,
  Operator,
  Player,
  PlayerAppliedTurboStarsFreeSpinsTemplate,
  PlayerKycV2,
  TGame,
  Transaction,
  TransactionListDto,
} from '@alien-platform/bo-core';
import type { BonusReleaseReportItem, GetAffiliateActivePlayers, OperatorBillingRecord } from '@alien-platform/bo-sdk';
import { User } from '@alien-platform/bo-sdk-v2';
import type { Card } from '@alien-platform/cms-bo';
import type {
  IAffiliateWithdrawPPF,
  IOperatorInvoice,
  IPlayerBonusRelease,
  IPlayerBonusSummary,
} from '@ap-db-worker/common';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  GeneralDrawer,
  type ActivitiesTransactionDetailsProps,
  type Admin,
  type Affiliate,
  type PlayerDetailsProps,
  type TableFilter,
} from '@/types/general';

import type { RootState } from '..';

interface OperatorGameDetailsProps {
  game: TGame;
  opId: string;
}
interface GeneralGameDetailsProps {
  gameId: string;
  operators?: Operator[];
}

interface DrawerContentSliceState {
  [GeneralDrawer.BANK_ACCOUNT]: BankAccount | null;
  [GeneralDrawer.BET_DETAILS]: Bets | null;
  [GeneralDrawer.DNS_RECORD]: unknown;
  [GeneralDrawer.TRANSACTION_DETAILS]: Transaction | null;
  [GeneralDrawer.PLAYER_DETAILS]: PlayerDetailsProps | null;

  [GeneralDrawer.ADMIN_DETAILS]: Admin | null;
  [GeneralDrawer.AFFILIATE_DETAILS]: Affiliate | null;
  [GeneralDrawer.GENERAL_GAME_DETAILS]: GeneralGameDetailsProps | null;
  [GeneralDrawer.OPERATOR_GAME_DETAILS]: OperatorGameDetailsProps | null;
  [GeneralDrawer.CHANNEL_DETAILS]: Channel | null;
  [GeneralDrawer.TABLE_FILTERS]: TableFilter | null;
  [GeneralDrawer.FEATURED_CARD_DETAILS]: { item: Card; index: number } | null;

  [GeneralDrawer.NAV_DRAWER]: boolean | null;
  [GeneralDrawer.OPERATOR_DETAILS]: { opId: string; parentOpId?: string } | null;
  [GeneralDrawer.NEW_TAG]: { currentTags: string[]; opId: string } | null;
  [GeneralDrawer.REFERRAL_CLAIM_HISTORY]: { player: Player } | null;
  [GeneralDrawer.REFERRED_PLAYERS_LIST]: {
    appliedFilters: GetAllPlayersRequestDto;
    title?: string;
  } | null;
  [GeneralDrawer.OPERATOR_BILLING_RECORD_DETAILS]: OperatorBillingRecord | null;
  [GeneralDrawer.BRAND_BILLING_RECORD_DETAILS]: OperatorBillingRecord | null;
  [GeneralDrawer.AFFILIATE_CLAIM_HISTORY]: {
    affId: string;
    opId: string;
    parentOpId?: string;
  } | null;
  [GeneralDrawer.AFFILIATE_WITHDRAWAL_HISTORY]: IAffiliateWithdrawPPF[] | null;
  [GeneralDrawer.AFD_DETAILS]: unknown;

  [GeneralDrawer.OPERATOR_AFFILIATE_PAYMENT_FORM]: {
    opId: string;
    affId: string;
  } | null;
  [GeneralDrawer.PLAYER_BONUS_DETAILS]: IPlayerBonusSummary | null;
  [GeneralDrawer.PLAYER_BONUS_RELEASE_DETAILS]: IPlayerBonusRelease | null;
  [GeneralDrawer.CF_TOKEN_DETAILS]: unknown;
  [GeneralDrawer.SPORT_FREEBET_DETAILS]: FreebetItem | null;
  [GeneralDrawer.SPORT_FREEBET_TEMPLATE_LIST]: { playerId: string } | null;
  [GeneralDrawer.CF_ZONE_DETAILS]: unknown;
  [GeneralDrawer.PLAYER_APPLIED_FREEBET_DETAILS]: PlayerAppliedTurboStarsFreeSpinsTemplate | null;
  [GeneralDrawer.ROLE_DETAILS]: unknown;
  [GeneralDrawer.PLAY_GAME]: unknown;
  [GeneralDrawer.BONUS_RELEASE_REPORT_DETAILS]: BonusReleaseReportItem | null;
  [GeneralDrawer.ACTIVITIES_TRANSACTION_DETAILS]: ActivitiesTransactionDetailsProps | null;
  [GeneralDrawer.AFFILIATE_ACTIVE_PLAYERS]: GetAffiliateActivePlayers | null;
  [GeneralDrawer.OPERATOR_BILLING_INVOICE_DETAILS]: IOperatorInvoice | null;
  [GeneralDrawer.PLAYER_CASHBACK_TRANSACTIONS_LIST]: {
    playerCode: string;
    opId: string;
  } | null;
  [GeneralDrawer.PLAYER_PROFIT_TRANSACTIONS_LIST]: {
    appliedFilters: GetAllPlayersRequestDto;
  } | null;
  [GeneralDrawer.DOMAIN_DETAILS]: { domainName: string; opId: string } | null;
  [GeneralDrawer.CF_ACCOUNT_DETAILS]: string | null;
  [GeneralDrawer.STAFF_DETAILS]: { opId: string; id: string } | null;
  [GeneralDrawer.KYC_DETAILS]: PlayerKycV2 | null;
}

const initialState: DrawerContentSliceState = {
  BET_DETAILS: null,
  TRANSACTION_DETAILS: null,
  PLAYER_DETAILS: null,
  GENERAL_GAME_DETAILS: null,
  OPERATOR_GAME_DETAILS: null,
  CHANNEL_DETAILS: null,
  TABLE_FILTERS: null,

  NAV_DRAWER: null,
  OPERATOR_DETAILS: null,
  ADMIN_DETAILS: null,
  AFFILIATE_DETAILS: null,
  NEW_TAG: null,
  FEATURED_CARD_DETAILS: null,
  REFERRAL_CLAIM_HISTORY: null,
  REFERRED_PLAYERS_LIST: null,
  OPERATOR_BILLING_RECORD_DETAILS: null,
  BRAND_BILLING_RECORD_DETAILS: null,
  AFFILIATE_WITHDRAWAL_HISTORY: null,
  AFFILIATE_CLAIM_HISTORY: null,
  DNS_RECORD: null,
  AFD_DETAILS: null,
  OPERATOR_AFFILIATE_PAYMENT_FORM: null,
  PLAYER_BONUS_DETAILS: null,
  PLAYER_BONUS_RELEASE_DETAILS: null,
  BANK_ACCOUNT: null,
  CF_TOKEN_DETAILS: null,
  SPORT_FREEBET_DETAILS: null,
  SPORT_FREEBET_TEMPLATE_LIST: null,
  CF_ZONE_DETAILS: null,
  PLAYER_APPLIED_FREEBET_DETAILS: null,
  ROLE_DETAILS: null,
  PLAY_GAME: null,
  BONUS_RELEASE_REPORT_DETAILS: null,
  ACTIVITIES_TRANSACTION_DETAILS: null,
  AFFILIATE_ACTIVE_PLAYERS: null,
  OPERATOR_BILLING_INVOICE_DETAILS: null,
  PLAYER_CASHBACK_TRANSACTIONS_LIST: null,
  PLAYER_PROFIT_TRANSACTIONS_LIST: null,
  DOMAIN_DETAILS: null,
  CF_ACCOUNT_DETAILS: null,
  STAFF_DETAILS: null,
  KYC_DETAILS: null,
};

export const drawerContentSlice = createSlice({
  name: 'drawerContent',
  initialState,
  reducers: {
    setBankAccountDetails: (state, action: PayloadAction<BankAccount | null>) => {
      state[GeneralDrawer.BANK_ACCOUNT] = action.payload;
    },
    setBetDetails: (state, action: PayloadAction<Bets>) => {
      state[GeneralDrawer.BET_DETAILS] = action.payload;
    },
    setCfTokenDetails: (state, action: PayloadAction<string>) => {
      state[GeneralDrawer.CF_ACCOUNT_DETAILS] = action.payload;
    },
    setCfZoneDetails: (state, action: PayloadAction<unknown>) => {
      state[GeneralDrawer.CF_ZONE_DETAILS] = action.payload;
    },
    setDnsDetails: (state, action: PayloadAction<unknown>) => {
      state[GeneralDrawer.DNS_RECORD] = action.payload;
    },
    setAfdDetails: (state, action: PayloadAction<unknown>) => {
      state[GeneralDrawer.AFD_DETAILS] = action.payload;
    },
    setRoleDetails: (state, action: PayloadAction<unknown>) => {
      state[GeneralDrawer.ROLE_DETAILS] = action.payload;
    },
    setTransactionDetails: (state, action: PayloadAction<Transaction>) => {
      state[GeneralDrawer.TRANSACTION_DETAILS] = action.payload;
    },
    setAdminDetails: (state, action: PayloadAction<Admin>) => {
      state[GeneralDrawer.ADMIN_DETAILS] = action.payload;
    },
    setAffiliateDetails: (state, action: PayloadAction<Affiliate>) => {
      state[GeneralDrawer.AFFILIATE_DETAILS] = action.payload;
    },
    setPlayerDetails: (state, action: PayloadAction<PlayerDetailsProps | null>) => {
      state[GeneralDrawer.PLAYER_DETAILS] = action.payload;
    },
    setActivitiesTransactionDetails: (state, action: PayloadAction<ActivitiesTransactionDetailsProps>) => {
      state[GeneralDrawer.ACTIVITIES_TRANSACTION_DETAILS] = action.payload;
    },
    setGeneralGameDetails: (state, action: PayloadAction<GeneralGameDetailsProps>) => {
      state[GeneralDrawer.GENERAL_GAME_DETAILS] = action.payload;
    },
    setPlayGameDetails: (state, action: PayloadAction<unknown>) => {
      state[GeneralDrawer.PLAY_GAME] = action.payload;
    },
    setOperatorGameDetails: (state, action: PayloadAction<OperatorGameDetailsProps>) => {
      state[GeneralDrawer.OPERATOR_GAME_DETAILS] = action.payload;
    },
    setChannelDetails: (state, action: PayloadAction<Channel>) => {
      state[GeneralDrawer.CHANNEL_DETAILS] = action.payload;
    },
    setTableFilters: (state, action: PayloadAction<TableFilter>) => {
      state[GeneralDrawer.TABLE_FILTERS] = action.payload;
    },

    setOperatorDetails: (state, action: PayloadAction<{ opId: string; parentOpId?: string } | null>) => {
      state[GeneralDrawer.OPERATOR_DETAILS] = action.payload;
    },
    setDomainDetails: (state, action: PayloadAction<{ domainName: string; opId: string } | null>) => {
      state[GeneralDrawer.DOMAIN_DETAILS] = action.payload;
    },
    setFeaturedCardDetails: (state, action: PayloadAction<{ item: Card; index: number }>) => {
      state[GeneralDrawer.FEATURED_CARD_DETAILS] = {
        item: action.payload.item,
        index: action.payload.index,
      };
    },

    setNewTag: (state, action: PayloadAction<{ currentTags: string[]; opId: string }>) => {
      state[GeneralDrawer.NEW_TAG] = action.payload;
    },
    setReferralClaimHistory: (state, action: PayloadAction<{ player: Player }>) => {
      state[GeneralDrawer.REFERRAL_CLAIM_HISTORY] = action.payload;
    },
    setReferredPlayers: (
      state,
      action: PayloadAction<{
        appliedFilters: GetAllPlayersRequestDto;
        title?: string;
      }>
    ) => {
      state[GeneralDrawer.REFERRED_PLAYERS_LIST] = action.payload;
    },
    setPlayerTransactionProfit: (
      state,
      action: PayloadAction<{
        appliedFilters: TransactionListDto;
      }>
    ) => {
      state[GeneralDrawer.PLAYER_PROFIT_TRANSACTIONS_LIST] = action.payload;
    },
    setOperatorBillingRecordDetails: (state, action: PayloadAction<OperatorBillingRecord>) => {
      state[GeneralDrawer.OPERATOR_BILLING_RECORD_DETAILS] = action.payload;
    },
    setBrandBillingRecordDetails: (state, action: PayloadAction<OperatorBillingRecord>) => {
      state[GeneralDrawer.BRAND_BILLING_RECORD_DETAILS] = action.payload;
    },

    setAffiliateClaimHistory: (
      state,
      action: PayloadAction<{
        affId: string;
        opId: string;
        parentOpId?: string;
      }>
    ) => {
      state[GeneralDrawer.AFFILIATE_CLAIM_HISTORY] = action.payload;
    },
    setAffiliateWithdrawalHistory: (state, action: PayloadAction<IAffiliateWithdrawPPF[]>) => {
      state[GeneralDrawer.AFFILIATE_WITHDRAWAL_HISTORY] = action.payload;
    },
    setOperatorAffiliatePaymentForm: (
      state,
      action: PayloadAction<{
        affId: string;
        opId: string;
      }>
    ) => {
      state[GeneralDrawer.OPERATOR_AFFILIATE_PAYMENT_FORM] = action.payload;
    },

    setPlayerBonusDetails: (state, action: PayloadAction<IPlayerBonusSummary>) => {
      state[GeneralDrawer.PLAYER_BONUS_DETAILS] = action.payload;
    },
    setPlayerBonusReleaseDetails: (state, action: PayloadAction<IPlayerBonusRelease>) => {
      state[GeneralDrawer.PLAYER_BONUS_RELEASE_DETAILS] = action.payload;
    },

    setSportFreeBetDetails: (state, action: PayloadAction<FreebetItem>) => {
      state[GeneralDrawer.SPORT_FREEBET_DETAILS] = action.payload;
    },
    setSportFreeBetTemplateList: (state, action: PayloadAction<{ playerId: string }>) => {
      state[GeneralDrawer.SPORT_FREEBET_TEMPLATE_LIST] = action.payload;
    },
    setPlayerAppliedFreeBetDetails: (state, action: PayloadAction<PlayerAppliedTurboStarsFreeSpinsTemplate>) => {
      state[GeneralDrawer.PLAYER_APPLIED_FREEBET_DETAILS] = action.payload;
    },
    setBonusReleaseReportDetails: (state, action: PayloadAction<BonusReleaseReportItem>) => {
      state[GeneralDrawer.BONUS_RELEASE_REPORT_DETAILS] = action.payload;
    },
    setAffiliateActivePlayers: (state, action: PayloadAction<GetAffiliateActivePlayers>) => {
      state[GeneralDrawer.AFFILIATE_ACTIVE_PLAYERS] = action.payload;
    },
    setOperatorBillingInvoiceDetails: (state, action: PayloadAction<IOperatorInvoice>) => {
      state[GeneralDrawer.OPERATOR_BILLING_INVOICE_DETAILS] = action.payload;
    },
    setPlayerCashbackTransactionsList: (state, action: PayloadAction<{ playerCode: string; opId: string }>) => {
      state[GeneralDrawer.PLAYER_CASHBACK_TRANSACTIONS_LIST] = action.payload;
    },
    setStaffDetails: (state, action: PayloadAction<User | null>) => {
      state[GeneralDrawer.STAFF_DETAILS] = action.payload;
    },
    setKycDetails: (state, action: PayloadAction<PlayerKycV2 | null>) => {
      state[GeneralDrawer.KYC_DETAILS] = action.payload;
    },
  },
});

// [GeneralDrawer.AFFILIATE_ACTIVE_PLAYERS]: GetAffiliateActivePlayers | null

// Export the action creators
export const {
  setBankAccountDetails,
  setBetDetails,
  setDnsDetails,
  setAfdDetails,
  setRoleDetails,
  setTransactionDetails,
  setPlayerDetails,
  setAdminDetails,
  setAffiliateDetails,
  setChannelDetails,
  setGeneralGameDetails,
  setOperatorGameDetails,
  setTableFilters,

  setOperatorDetails,
  setNewTag,
  setFeaturedCardDetails,
  setReferralClaimHistory,
  setReferredPlayers,
  setOperatorBillingRecordDetails,
  setBrandBillingRecordDetails,
  setAffiliateClaimHistory,
  setAffiliateWithdrawalHistory,
  setOperatorAffiliatePaymentForm,
  setPlayerBonusDetails,
  setPlayerBonusReleaseDetails,

  setCfTokenDetails,
  setSportFreeBetDetails,
  setSportFreeBetTemplateList,
  setCfZoneDetails,
  setPlayerAppliedFreeBetDetails,
  setPlayGameDetails,
  setBonusReleaseReportDetails,
  setActivitiesTransactionDetails,
  setAffiliateActivePlayers,
  setOperatorBillingInvoiceDetails,
  setPlayerCashbackTransactionsList,
  setPlayerTransactionProfit,
  setDomainDetails,
  setStaffDetails,
  setKycDetails,
} = drawerContentSlice.actions;

export const getDrawerContent = (state: RootState): DrawerContentSliceState => state.drawerContent;
